import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardMultiplier } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardMultiplier';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { laptopAndBelow } from '@sorare/core/src/style/mediaQuery';

import { TargetsCarousel } from '#src/components/leaderboard/TargetsCarousel/index.tsx';
import { ThresholdProgressBar } from '#src/components/leaderboard/ThresholdProgressBar/index.tsx';
import { ContenderFooter } from '#src/components/lineup/Contender/ContenderFooter/index.tsx';
import { ContenderLiveProjectedRanking } from '#src/components/lineup/Contender/ContenderLiveProjectedRanking/index.tsx';
import { LineupButtons } from '#src/components/lineup/LineupButtons/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import type { TeamLineupFooter_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const StyledVertical = styled(Vertical)`
  &.withSpacing {
    margin-bottom: var(--quadruple-unit);
  }

  @media ${laptopAndBelow} {
    margin-bottom: var(--quadruple-unit);
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  max-width: 440px;
  margin: auto;
`;

type Props = {
  so5LeaderboardContender: TeamLineupFooter_so5LeaderboardContender;
  onClickPrizePool?: () => void;
  onClickBonus?: () => void;
};

export const TeamLineupFooter = ({
  so5LeaderboardContender,
  onClickPrizePool,
  onClickBonus,
}: Props) => {
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const { so5Leaderboard, so5Lineup, managerTeam } = so5LeaderboardContender;
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);

  const ranking = so5Lineup?.so5Rankings.at(0);

  const showRewardsSubFooter = !!so5Lineup && so5Lineup.rewardMultiplier > 1;

  const buttons = (
    <LineupButtons
      standalone
      so5Leaderboard={so5Leaderboard}
      so5Lineup={so5Lineup}
      managerTeam={managerTeam}
      onDelete={() => {
        navigate(
          generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS, {
            params: {
              eventType:
                so5LeaderboardContender.so5Leaderboard.so5Fixture.type.toLowerCase(),
              fixture: so5LeaderboardContender.so5Leaderboard.so5Fixture.slug,
            },
          })
        );
      }}
    />
  );

  const divisionChangeConfigBar = !isLaptopAndAbove && (
    <TargetsCarousel
      so5Leaderboard={so5Leaderboard}
      so5Lineup={so5Lineup || undefined}
    />
  );

  const thresholdProgressBar = !isLaptopAndAbove &&
    (so5Leaderboard.rewardsConfig.conditional || []).length > 0 && (
      <ThresholdProgressBar
        so5Leaderboard={so5Leaderboard}
        so5Lineup={so5Lineup}
      />
    );

  const contenderFooter = (
    <ContenderFooter
      so5LeaderboardContender={so5LeaderboardContender}
      onClickPrizePool={!isLaptopAndAbove ? onClickPrizePool : undefined}
      onClickBonus={!isLaptopAndAbove ? onClickBonus : undefined}
    />
  );

  if (isUpcoming) {
    return (
      <Container>
        <StyledVertical gap={2} className="withSpacing">
          {contenderFooter}
          {buttons}
        </StyledVertical>
      </Container>
    );
  }

  return (
    <Container>
      <FooterWrapper>
        <StyledVertical gap={2} className="withSpacing">
          {contenderFooter}
          {divisionChangeConfigBar}
          {thresholdProgressBar}
          <Horizontal center gap={1.5}>
            {!so5Leaderboard.pveOnly && (
              <ContenderLiveProjectedRanking so5Ranking={ranking} />
            )}
            {!!showRewardsSubFooter && (
              <RewardMultiplier lineup={so5Lineup} variant={Variant.TEXT} />
            )}
          </Horizontal>
        </StyledVertical>
      </FooterWrapper>
    </Container>
  );
};

TeamLineupFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment TeamLineupFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        mainRarityType
        pveOnly
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
        rewardsConfig {
          conditional {
            id
            score
            target {
              slug
            }
          }
        }
        ...LineupButtons_so5Leaderboard
        ...ThresholdProgressBar_so5Leaderboard
        ...TargetsCarousel_so5Leaderboard
      }
      so5Lineup {
        id
        so5Rankings {
          id
          targetedRewardWithEligibility {
            reward {
              id
              score
              target {
                slug
                name
                color
                pictureUrl
              }
            }
          }
          ...ContenderLiveFooter_so5Ranking
        }
        ...RewardMultiplier_lineup
        ...LineupButtons_so5Lineup
        ...ThresholdProgressBar_so5Lineup
        ...TargetsCarousel_so5Lineup
      }
      managerTeam {
        id
        ...LineupButtons_managerTeam
      }
      ...ContenderFooter_so5LeaderboardContender
    }
    ${isFixtureOpened.fragments.so5Fixture}
    ${LineupButtons.fragments.so5Leaderboard}
    ${LineupButtons.fragments.so5Lineup}
    ${LineupButtons.fragments.managerTeam}
    ${TargetsCarousel.fragments.so5Leaderboard}
    ${TargetsCarousel.fragments.so5Lineup}
    ${ThresholdProgressBar.fragments.so5Leaderboard}
    ${ThresholdProgressBar.fragments.so5Lineup}
    ${ContenderFooter.fragments.so5LeaderboardContender}
    ${ContenderLiveProjectedRanking.fragments.so5Ranking}
    ${RewardMultiplier.fragments.lineup}
  ` as TypedDocumentNode<TeamLineupFooter_so5LeaderboardContender>,
};
